import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { Download } from 'lucide-react'
import { getAnalytics } from '@/lib/api'
import type { AnalyticsData } from '@/schemas'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

type Range = '7d' | '30d' | '90d'

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
]

const FUNNEL_STAGES = ['new', 'qualified', 'contacted', 'replied', 'converted'] as const

function pct(a: number, b: number) {
  if (b === 0) return '—'
  return `${Math.round((a / b) * 100)}%`
}

function exportCSV(data: AnalyticsData) {
  const rows = [
    ['Date', 'Leads added', 'Qualified', 'Replied'],
    ...(data.daily_leads ?? []).map((d) => [d.date ?? '', d.added ?? 0, d.qualified ?? 0, d.replied ?? 0]),
  ]
  const csv = rows.map((r) => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `analytics-${data.range}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function Analytics() {
  const [range, setRange] = useState<Range>('30d')

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', range],
    queryFn: () => getAnalytics(range),
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pipeline performance and sequence results.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setRange(opt.value)}
                className={[
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  range === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {data && (
            <Button variant="outline" size="sm" onClick={() => exportCSV(data)}>
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Line chart */}
      <div className="rounded-lg border bg-card p-5">
        <h2 className="text-sm font-semibold mb-4">Daily leads</h2>
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : !data?.daily_leads?.length ? (
          <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
            No data for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={(data.daily_leads ?? []).map((d) => {
                let label = d.date ?? ''
                try { if (d.date) label = format(parseISO(d.date), 'd MMM') } catch { /* skip */ }
                return { ...d, label }
              })}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="added"
                name="Added"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="qualified"
                name="Qualified"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="replied"
                name="Replied"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Funnel */}
      <div className="rounded-lg border bg-card p-5">
        <h2 className="text-sm font-semibold mb-4">Pipeline funnel</h2>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : !data?.funnel ? null : (
          <div className="space-y-1">
            {FUNNEL_STAGES.map((stage, i) => {
              const count = data.funnel[stage] ?? 0
              const prev = i > 0 ? (data.funnel[FUNNEL_STAGES[i - 1]] ?? 0) : null
              const max = (data.funnel.new ?? 0) || 1
              const barWidth = Math.round((count / max) * 100)

              return (
                <div key={stage}>
                  {i > 0 && prev !== null && (
                    <div className="flex justify-center py-0.5">
                      <span className="text-xs text-muted-foreground">
                        {pct(count, prev)} conversion
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="w-24 text-xs font-medium capitalize text-right shrink-0">
                      {stage}
                    </span>
                    <div className="flex-1 h-7 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-primary/80 rounded transition-all"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <span className="w-12 text-xs text-right tabular-nums">{count}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Sequence performance */}
      <div className="rounded-lg border bg-card">
        <div className="px-5 py-3 border-b">
          <h2 className="text-sm font-semibold">Sequence performance</h2>
        </div>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : !data?.sequence_performance?.length ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No sequences.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Enrolled</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Replied</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Converted</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Reply rate</th>
                </tr>
              </thead>
              <tbody>
                {data.sequence_performance?.map((s) => (
                  <tr key={s.sequence_id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2.5">{s.name}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.enrolled}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.replied}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.converted}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {s.reply_rate != null ? `${(s.reply_rate * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ICP performance */}
      <div className="rounded-lg border bg-card">
        <div className="px-5 py-3 border-b">
          <h2 className="text-sm font-semibold">ICP performance</h2>
        </div>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : !data?.icp_performance?.length ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No ICP data.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">ICP</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Leads</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Avg score</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                    Qualification rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.icp_performance?.map((icp) => (
                  <tr key={icp.icp_id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2.5">{icp.name}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{icp.lead_count}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {icp.avg_score != null ? icp.avg_score.toFixed(1) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {icp.qualification_rate != null ? `${(icp.qualification_rate * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
